<script setup lang="ts">
import type AccountUser from '~/models/AccountUser';
import type { Offer } from '~/models/Offers';
import type Housing from '~/models/Housing';
import type Search from '~/models/Search';
import type User from '~/models/User';

const emit = defineEmits(['updated', 'update:user-interest', 'badge-clicked']);

const props = defineProps({
  housingId: {
    type: Number,
    required: true,
  },
  offer: {
    type: Object as PropType<Offer>,
    required: false,
  },
  housing: {
    type: Object as PropType<Housing>,
    required: true,
  },
  user: {
    type: Object as PropType<User | AccountUser>,
    required: true,
  },
  search: {
    type: Object as PropType<Search>,
    required: true,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  isUsersOwnHousing: {
    type: Boolean,
    default: false,
  },
});

const { getTrackingConfig } = useTrackingConfig();
const trackingImageGallery = getTrackingConfig('ad', 'buttons','imageGallery');
const trackingContactButton = getTrackingConfig('ad', 'buttons','contactFunction');
const trackingWatchButton = getTrackingConfig('ad', 'buttons','watchFunction');

const accountStore = useAccountStore();
const currentOfferActions = useCurrentOfferActions();
const isAuthenticated = useAuthState();
const { showToast } = useToast();
const { hasMissingField } = useMissingFields();
const isDesktop = useIsDesktop();
const { t } = useI18n({
  useScope: 'local',
});

const watchListUpdateInProgress = ref<boolean>(false);
const reportModalOpen = ref<boolean>(false);

const interestBoxValue = computed(() =>
  props?.offer?.actions?.isInterested === true ? 'interested' : props?.offer?.actions?.isInterested === false ? 'disinterested' : null,
);

const exchangeType = computed(() => {
  return props?.offer?.match?.chainLength != null && props?.offer?.match?.chainLength > 2 ? 'ring' : 'direct';
});

const moreActionsType = ref('');
const conversionModalStatus = ref('close');

const onRestrictedFeatureClicked = (type: string) => {
  moreActionsType.value = type;
  handleNoteRegisteredInfo('open');
};

const onReported = () => {
  showToast('success', t('reported-title'), t('reported-description'));
};

const onUpdate = (update: Housing) => {
  emit('updated', update);
};

function handleNoteRegisteredInfo(action: 'open' | 'close') {
  conversionModalStatus.value = action;
}

function updateMoreActionsType(type: string) {
  moreActionsType.value = type;
  handleNoteRegisteredInfo('open');
}

async function onWatchListAction(value: boolean) {
  watchListUpdateInProgress.value = true;
  try {
    await currentOfferActions.setWatchStatus(value);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    showToast('error', t('watch-list.error'), t('watch-list.error-message'));
  } finally {
    watchListUpdateInProgress.value = false;
  }
}
</script>
<template>
  <div class="tw-housing">
    <tw-profile-completion
      v-if="accountStore.housing?.completionStatus && isUsersOwnHousing && isEditing"
      :is-complete="accountStore.housing.completionStatus.isComplete"
      :progress="accountStore.housing.completionStatus.percentage"
      :layout="isDesktop ? 'row' : 'column'"
      :ring-size="isDesktop ? 80 : 60"
      :show-button="false"
    />
    <header v-if="housing" class="tw-housing-header">
      <div class="tw-housing-header-carousel">
        <tw-housing-carousel
          :housing="housing"
          class="tw-housing-header-carousel-image"
          :data-page="trackingImageGallery.dataPage"
          :data-tracking-id="trackingImageGallery.dataTrackingId"
        />
        <nuxt-link :to="{ name: 'housing-photos' }" class="tw-housing-header-carousel-link" :aria-label="$t('edit-photos')">
          <tw-housing-btn-edit v-if="isEditing" :has-missing-field="hasMissingField(props.housing.type.id, ['images'], accountStore.housing?.completionStatus.missingFields || [])" />
        </nuxt-link>
      </div>
      <div class="tw-housing-header-map">
        <tw-housing-map v-if="isAuthenticated" :housing="housing" />
        <tw-housing-map-hidden v-else />
      </div>
    </header>
    <div :class="['tw-housing-content', { 'no-content': !housing }]">
      <section class="tw-housing-content-section">
        <tw-housing-info
          v-if="housing"
          :user="user"
          :offer="offer"
          :search="search"
          :housing="housing"
          :is-editing="isEditing"
          :is-logged-in="isAuthenticated"
          :is-users-own-housing="isUsersOwnHousing"
          :interest-box-value="interestBoxValue"
          :exchange-type="exchangeType"
          @updated="onUpdate"
          @badge-clicked="emit('badge-clicked')"
          @update:user-interest="emit('update:user-interest', $event)"
        />
        <div v-else class="not-found">
          <tw-housing-not-found :housing-id="housingId" />
        </div>
      </section>
      <aside v-if="housing && user" class="tw-housing-content-actions">
        <tw-housing-info-card
          v-if="housing && user"
          :user="user"
          :offer="offer"
          :housing="housing"
          :is-editing="isEditing"
          :is-logged-in="isAuthenticated"
          :is-users-own-housing="isUsersOwnHousing"
          :watch-list-update-in-progress="watchListUpdateInProgress"
          @update:watch-list="onWatchListAction"
          @update:more-actions-type="updateMoreActionsType($event)"
        />
        <tw-rt-interest-box
          v-if="isAuthenticated && !isUsersOwnHousing && offer && offer.match"
          box-type="thumbs"
          :exchange-type="exchangeType"
          :active-button="interestBoxValue"
          @interested="emit('update:user-interest', true)"
          @disinterested="emit('update:user-interest', false)"
          @reset-interest="emit('update:user-interest', null)"
          @activatePeriod="emit('update:user-interest')"
        />
      </aside>
    </div>
    <aside :class="['tw-housing-bottom-bar', { 'is-logged-in': isAuthenticated }]">
      <TwHousingBtnContact v-if="isAuthenticated && props?.offer" :housing="housing" :disabled="isEditing || isUsersOwnHousing || !housing" :trade-variant="matchTypeByChainLength(props?.offer?.match ? props?.offer.match.chainLength : 0)" />
      <TwUiAction
        v-else
        class="tw-tracking"
        :is="'button'"
        type="button"
        variant="button-primary"
        @click="onRestrictedFeatureClicked('contact')"
        :disabled="isEditing || isUsersOwnHousing || !housing"
        :data-page="trackingContactButton.dataPage"
        :data-tracking-id="trackingContactButton.dataTrackingId"
      >
        {{ $t('to-contact') }}
      </TwUiAction>

      <TwButtonWatchList
        v-if="!isAuthenticated"
        class="tw-tracking"
        :state="'removed'"
        :variant="'icon'"
        :disabled="isEditing || isUsersOwnHousing"
        @click="onRestrictedFeatureClicked('toggle_like')"
        :data-tracking-id="trackingWatchButton.dataTrackingId"
        :data-page="trackingWatchButton.dataPage"
        data-button-icon="heart"
      />

      <TwButtonWatchList
        v-else-if="!offer?.actions.watched"
        :state="'removed'"
        :variant="'icon'"
        :loading="watchListUpdateInProgress"
        :disabled="isEditing || isUsersOwnHousing"
        @click="onWatchListAction(true)"
      />

      <TwButtonWatchList
        v-else
        :state="'added'"
        :variant="'icon'"
        :loading="watchListUpdateInProgress"
        :disabled="isEditing || isUsersOwnHousing"
        @click="onWatchListAction(false)"
      />

      <tw-ui-modal class="tw-housing-content-actions-more-modal">
        <template #trigger="{ toggle, attrs }">
          <button
            class="tw-housing-content-actions-more-modal-trigger"
            type="button"
            v-bind="attrs"
            @click="toggle()"
            :aria-label="$t('button-prefixed', { button: $t('more-actions') })"
          >
            <Icon name="bi:three-dots" class="ellipsis-icon" />
          </button>
        </template>
        <template #title>
          {{ $t('more-actions') }}
        </template>
        <template #default="{ close }">
          <ul class="tw-housing-content-actions-more-modal-actions">
            <li>
              <tw-housing-btn-share class="" :housing="housing" :disabled="isEditing || !housing">
                <template #button="{ toggle, attrs }">
                  <button type="button" class="tw-housing-content-actions-more-modal-btn-share" v-bind="attrs" @click="toggle">
                    {{ $t('to-share-offer') }}
                  </button>
                </template>
              </tw-housing-btn-share>
            </li>
            <li v-if="isAuthenticated">
              <button
                type="button"
                class="tw-housing-content-actions-more-modal-btn-report"
                @click="reportModalOpen = true"
                :disabled="isEditing || isUsersOwnHousing"
              >
                {{ $t('to-report-offer') }}
              </button>
              <tw-report-housing-card-modal
                :housing="housing"
                :state="reportModalOpen"
                @close="reportModalOpen = false"
                @reported="onReported"
                :disabled="isEditing || isUsersOwnHousing || !housing"
              />
            </li>
            <li v-else>
              <button
                type="button"
                class="tw-housing-content-actions-more-modal-btn-report"
                @click="
                  () => {
                    close();
                    onRestrictedFeatureClicked('report');
                  }
                "
                :disabled="isEditing || isUsersOwnHousing || !housing"
              >
                {{ $t('to-report-offer') }}
              </button>
            </li>
          </ul>
        </template>
      </tw-ui-modal>
    </aside>
    <tw-modal-conversion
      :more-actions-type="moreActionsType"
      :status="conversionModalStatus"
      tracking-page="ad"
      tracking-sub-category="buttons"
      tracking-area="modalRegistration"
      @close-conversion-modal="handleNoteRegisteredInfo('close')"
    />
  </div>
</template>

<style scoped lang="scss">
@import '~/assets/styles/tw-animations.scss';
@import '~/assets/styles/tw-mixins.scss';
@import '~/assets/styles/tw-form.scss';
.tw-housing {
  display: flex;
  position: relative;
  flex-direction: column;
  @include tw-is-desktop {
    gap: 2rem;
  }
}
.tw-housing-header {
  overflow: hidden;
  @include tw-is-desktop {
    gap: 2rem;
    display: grid;
    grid-template-columns: 2fr 1fr;
  }
}
.tw-housing-header-carousel {
  width: 100%;
  position: relative;
  display: inline-block;
}
.tw-housing-header-carousel > * {
  max-width: 100%;
  display: inline-block;
}
.tw-housing-header-carousel-link {
  z-index: 10;
  bottom: 1rem;
  right: 1rem;
  position: absolute;
}
.tw-housing-header-map {
  display: none;
  @include tw-is-desktop {
    overflow: hidden;
    display: inline-block;
  }
}
.tw-housing-content {
  @include tw-is-desktop {
    gap: 4rem;
    padding: 0;
    display: grid;
    grid-template-columns: 5fr 3fr;
  }
  &.no-content {
    grid-template-columns: 1fr;
  }
}
.tw-housing-content-section {
  flex-grow: 1;
  display: flex;
  flex-shrink: 1;
  flex-direction: column;
  & > div {
    position: relative;
  }
}
:deep(.not-found) {
  padding-top: 3rem;
}
.tw-housing-content-actions {
  display: none;
  @include tw-is-desktop {
    display: inline-block;
  }
}
.tw-housing-bottom-bar {
  left: 0;
  right: 0;
  gap: 1rem;
  bottom: 0;
  z-index: 9;
  width: 100%;
  display: grid;
  position: fixed;
  @include tw-shadow;
  background-color: $white;
  justify-content: space-around;
  border-top-left-radius: 0.35rem;
  border-top-right-radius: 0.35rem;
  grid-template-columns: 3fr 1fr 1fr;
  padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom, 2rem));
  @include tw-is-desktop {
    display: none;
  }
}

.tw-housing-bottom-bar.is-logged-in {
  padding: 1rem;
  bottom: calc(4rem + env(safe-area-inset-bottom, 2rem));
}

.tw-housing-content-actions-btn-like,
.tw-housing-content-actions-more-modal-trigger {
  @include tw-button-outlined;
  border: none;
  color: $gray;
  padding: 0.5rem 1rem;
  &:not(:disabled):focus,
  &:not(:disabled):hover,
  &:not(:disabled):active,
  &:not(:disabled):active {
    color: $gray;
    border: none;
    box-shadow: none;
    background-color: transparent;
  }
}
.tw-housing-content-actions-more-modal-actions {
  button {
    width: 100%;
    border: none;
    font-size: 1rem;
    text-align: left;
    background: none;
    border-radius: 0;
    padding: 0.75rem 0.5rem;
    &:hover {
      background-color: $lightGray;
    }
  }
}
.tw-housing-edit-modal-trigger {
  top: 0;
  right: 0;
  position: absolute;
}
.ellipsis-icon,
.heart-icon {
  width: 2rem;
  height: 2rem;
}
.heart-icon-loading {
  @include tw-pulse;
}

.tw-lightbox {
  margin-bottom: 1rem;
  @include tw-is-desktop {
    margin: 1rem 0 0;
  }
}
</style>
<i18n lang="json">
{
  "de": {
    "restricted-feature-title": "Info",
    "restricted-feature-description": "Bitte logge dich ein um diese Funktion nutzen zu können",
    "reported-title": "Erfolgreich",
    "reported-description": "Vielen Dank für dein Feedback. Wir schauen uns die Beschwerde schnellstmöglich an.",
    "watch-list": {
      "success": "Erfolg",
      "success-message": "Merkliste erfolgreich aktualisiert.",
      "error": "Fehler",
      "error-message": "Die Merkliste konnte leider nicht aktualisiert werden. Versuche es bitte später noch einmal."
    }
  },
  "en": {
    "restricted-feature-title": "Info",
    "restricted-feature-description": "Please log in to use this feature",
    "reported-title": "Success",
    "reported-description": "Thank you for your feedback. We will respond as soon as possible.",
    "watch-list": {
      "success": "Success",
      "success-message": "Watch list successfully updated.",
      "error": "Error",
      "error-message": "Unfortunately, the watch list could not be updated. Please try again later."
    }
  }
}
</i18n>
